import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, ShieldAlert, Users, Layers, Timer } from "lucide-react";
import { api } from "@/lib/api";

export default function LiveMonitor() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/admin/live").then(({ data }) => setData(data)).catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  if (!data) return <div className="text-slate-400 font-semibold py-20 text-center">Connecting to live feed…</div>;

  return (
    <div data-testid="live-monitor">
      <div className="flex items-center gap-3">
        <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#138808] opacity-60" /><span className="relative inline-flex rounded-full h-3 w-3 bg-[#138808]" /></span>
        <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Live Monitor</h1>
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400 ml-2">Status: {data.competition_status}</span>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400"><Users className="w-3.5 h-3.5" /> Playing Now</div><div className="font-timer text-3xl font-bold text-[#138808] mt-1" data-testid="live-playing">{data.live_participants.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400"><Layers className="w-3.5 h-3.5" /> Categories Live</div><div className="font-timer text-3xl font-bold text-[#FF9933] mt-1">{data.categories.filter((c) => c.unlocked).length}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400"><Timer className="w-3.5 h-3.5" /> Recent Answers</div><div className="font-timer text-3xl font-bold text-[#003366] mt-1">{data.recent_answers.length}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-5"><div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400"><ShieldAlert className="w-3.5 h-3.5" /> Recent Flags</div><div className="font-timer text-3xl font-bold text-red-600 mt-1">{data.recent_events.length}</div></div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 font-heading font-bold text-slate-900">Active Participants</div>
          {data.live_participants.length === 0 && <div className="p-8 text-center text-sm text-slate-400" data-testid="live-empty">No one is in a quiz right now.</div>}
          <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
            {data.live_participants.map((p) => (
              <div key={p.user_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`live-row-${p.user_id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm text-slate-800 truncate">{p.display_name} {p.inactive && <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded ml-1">IDLE</span>}</div>
                  <div className="text-xs text-slate-400">{p.category} • Q{p.question_index}/{p.total}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-timer font-bold text-[#FF9933]">{p.score}</div>
                  <div className="text-[10px] text-slate-400">{p.correct} correct</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 font-heading font-bold text-slate-900">Live Leaderboard</div>
            <div className="divide-y divide-slate-100">
              {data.leaderboard.slice(0, 8).map((e) => (
                <div key={e.user_id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700"><span className="font-timer text-slate-400 mr-2">#{e.rank}</span>{e.display_name}</span>
                  <span className="font-timer font-bold text-[#FF9933]">{e.score}</span>
                </div>
              ))}
              {data.leaderboard.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No scores yet.</div>}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 font-heading font-bold text-slate-900">Category Status</div>
            <div className="divide-y divide-slate-100">
              {data.categories.map((c) => (
                <div key={c.id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                  <span className="font-semibold text-slate-700">{c.order}. {c.name}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${c.unlocked ? "bg-[#138808]/10 text-[#138808]" : "bg-slate-100 text-slate-400"}`}>
                    {c.unlocked ? `${c.completed}/${c.started} done` : "Locked"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 font-heading font-bold text-slate-900">Recent Answers</div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {data.recent_answers.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 py-2.5 flex items-center justify-between text-sm">
                <span className="text-slate-600"><b className="text-slate-800">{a.display_name}</b> • {a.category}</span>
                <span className={`font-timer font-bold ${a.correct ? "text-[#138808]" : "text-red-500"}`}>{a.correct ? `+${a.points}` : "0"} <span className="text-[10px] text-slate-400 font-sans">{(a.response_ms / 1000).toFixed(1)}s</span></span>
              </motion.div>
            ))}
            {data.recent_answers.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No answers recorded yet.</div>}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100 font-heading font-bold text-slate-900">Suspicious Events</div>
          <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {data.recent_events.map((e) => (
              <div key={e.id} className="px-5 py-2.5 flex items-center justify-between text-sm">
                <span className="text-slate-600"><b className="text-slate-800">{e.display_name || e.user_id}</b> • {e.type.replace(/_/g, " ")}</span>
                <span className="text-xs text-slate-400">{new Date(e.ts).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" })}</span>
              </div>
            ))}
            {data.recent_events.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No flags. All clean.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
